import React, { useCallback } from "react";
import classNames from "../../../utils/classNames";
import { Entity } from "../../../utils/constants";
import useDataTable, { Property } from "./useDataTable";

interface Props {
  entity: Entity;
  property?: Property<any>;
  className?: string;
}

const TableHeaderCell: React.FC<Props> = ({ entity, property, className }) => {
  const sortOrder = useDataTable(
    useCallback((state) => state.sortOrder[entity], [entity])
  );
  const updateSortOrder = useDataTable((state) => state.updateSortOrder);

  const handleColumnSorting = useCallback(() => {
    if (!property?.sortIteratee) return;

    if (
      sortOrder?.iteratee === property.sortIteratee &&
      sortOrder?.order === "asc"
    ) {
      updateSortOrder(entity, {
        iteratee: property.sortIteratee,
        order: "desc"
      });
    } else if (
      sortOrder?.iteratee === property.sortIteratee &&
      sortOrder?.order === "desc"
    ) {
      updateSortOrder(entity, undefined);
    } else {
      updateSortOrder(entity, {
        iteratee: property.sortIteratee,
        order: "asc"
      });
    }
  }, [
    entity,
    property?.sortIteratee,
    sortOrder?.iteratee,
    sortOrder?.order,
    updateSortOrder
  ]);

  const handleOnClick = useCallback(() => {
    if (property?.onHeaderClick) {
      property.onHeaderClick();
    } else {
      handleColumnSorting();
    }
  }, [handleColumnSorting, property]);

  return (
    <th
      className={classNames(
        "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-gray-50 dark:bg-gray-700 dark:text-gray-300 text-gray-500",
        property?.sortIteratee && "cursor-pointer",
        className
      )}
      style={{ zIndex: 1, ...property?.style }}
      onClick={handleOnClick}
    >
      <div className="flex items-center justify-start">
        {property?.title}
        {sortOrder &&
          property &&
          sortOrder.iteratee === property?.sortIteratee &&
          (sortOrder.order === "asc" ? (
            <svg
              className="ml-2 h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          ) : (
            <svg
              className="ml-2 h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l2.293-2.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          ))}
      </div>
    </th>
  );
};

export default TableHeaderCell;
